import { useState, useMemo, useCallback } from 'react';
import SiteVisitFilters from './SiteVisitFilters.jsx';
import SiteScheduleCheckbox from './SiteScheduleCheckbox.jsx';
import { useRailsApi } from '../../hooks/useRailsApi.js';
import styles from './DeliverableAssociationSelect.module.css';
import { LoaderCircle, Plus, Square, SquareCheck } from 'lucide-react'

export default function DeliverableAssociationSelect({
  deliverable,
  nameForSites = 'Sites',
  url,
  siteSchedules,
  gridReturnUrl
}) {
  const { isLoading, error, post } = useRailsApi();

  const { sites, schedules, initialMap } = useMemo(() => {
    const sitesMap = new Map();
    const schedulesMap = new Map();
    const ssMap = new Map();

    siteSchedules.forEach((ss) => {
      sitesMap.set(ss.trial_site_local_id, { id: ss.trial_site_local_id, name: ss.site_name });
      schedulesMap.set(ss.trial_schedule_local_id, { id: ss.trial_schedule_local_id, name: ss.schedule_name, order: ss.schedule_order });
      const key = `${ss.trial_site_local_id}-${ss.trial_schedule_local_id}`;
      ssMap.set(key, { ...ss, key });
    });

    return {
      sites: Array.from(sitesMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
      schedules: Array.from(schedulesMap.values()).sort((a, b) => a.order - b.order),
      initialMap: ssMap,
    };
  }, [siteSchedules]);

  const [siteScheduleMap, setSiteScheduleMap] = useState(() => new Map(initialMap));
  const [selectedSiteIds, setSelectedSiteIds] = useState(() => new Set(sites.map((s) => s.id)));
  const [selectedScheduleIds, setSelectedScheduleIds] = useState(() => new Set(schedules.map((s) => s.id)));

  const sitesToRender = (scheduleId) => {
    return sites.filter((site) => {
      const ss = siteScheduleMap.get(`${site.id}-${scheduleId}`);
      if (!ss) return false;
      return ss.selected || (selectedScheduleIds.has(scheduleId) && selectedSiteIds.has(site.id));
    });
  };

  const handleFilterChange = useCallback((type, id, checked) => {
    if (type === 'site') {
      setSelectedSiteIds((prev) => {
        const next = new Set(prev);
        if (id === 0) {
          return checked ? new Set(sites.map((s) => s.id)) : new Set();
        }
        if (checked) { next.add(id); } else { next.delete(id); }
        return next;
      });
    } else if (type === 'schedule') {
      setSelectedScheduleIds((prev) => {
        const next = new Set(prev);
        if (id === 0) {
          return checked ? new Set(schedules.map((s) => s.id)) : new Set();
        }
        if (checked) { next.add(id); } else { next.delete(id); }
        return next;
      });
    }
  }, [sites, schedules]);

  const handleSiteScheduleSelected = useCallback((key, checked) => {
    setSiteScheduleMap((prev) => {
      const next = new Map(prev);
      const ss = next.get(key);
      if (!ss) return prev;
      next.set(key, { ...ss, selected: checked });
      return next;
    });
  }, []);

  const handleSelectAllSiteSchedules = () => {
    setSiteScheduleMap((prev) => {
      const next = new Map(prev);
      for (const [key, ss] of next) {
        if (!ss.selected && selectedScheduleIds.has(ss.trial_schedule_local_id) && selectedSiteIds.has(ss.trial_site_local_id)) {
          next.set(key, { ...ss, selected: true });
        }
      }
      return next;
    });
  };

  const handleDeselectAllSiteSchedules = () => {
    setSiteScheduleMap((prev) => {
      const next = new Map(prev);
      for (const [key, ss] of next) {
        if (ss.selected) {
          next.set(key, { ...ss, selected: false });
        }
      }
      return next;
    });
  };

  const submit = async () => {
    const selectedSiteSchedules = Array.from(siteScheduleMap.values()).filter((ss) => ss.selected);
    const payload = {
      trial_deliverable_association_form: {
        site_schedules: selectedSiteSchedules.map((ss) => ({
          trial_site_local_id: ss.trial_site_local_id,
          trial_schedule_local_id: ss.trial_schedule_local_id,
          trial_site_schedule_local_id: ss.local_id,
        })),
      },
      grid_return_url: gridReturnUrl,
    };
    const result = await post(url, payload);
    if (result?.success) {
      if (result.htmx) {
        window.htmx.ajax('GET', result.redirect_url, { swap: 'innerHTML show:window:top transition:true', target: '#trials-main' });
      } else {
        window.location.href = result.redirect_url;
      }
    }
  };

  return (
    <div className="deliverable-association-select d-flex flex-column gap-2">
      <div className="my-3">
        <span className="fs-4 p-2">
          <span className="text-muted">Connecting {nameForSites} and Visits to Deliverable:</span>
          {' '}{deliverable.title}
        </span>
      </div>

      {error && (
        <div className="w-100 my-3 alert alert-danger">
          Something went wrong. Please try again later.
        </div>
      )}

      <SiteVisitFilters
        selectedSiteIds={selectedSiteIds}
        selectedScheduleIds={selectedScheduleIds}
        sites={sites}
        schedules={schedules}
        nameForSites={nameForSites}
        onFilterChange={handleFilterChange}
      />

      <div className="d-flex justify-content-end align-items-end">
        <div className="d-flex gap-2">
          <div>
            <button className="btn btn-secondary btn-sm" type="button" onClick={handleSelectAllSiteSchedules}>
              <div className="btn-addon">
                <SquareCheck size={16} strokeWidth={2} aria-hidden />
              </div>
              Select All in View
            </button>
          </div>
          <div>
            <button className="btn btn-warning btn-sm" type="button" onClick={handleDeselectAllSiteSchedules}>
              <div className="btn-addon">
                <Square size={16} strokeWidth={2} aria-hidden />
              </div>
              Deselect All
            </button>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column gap-3 mt-3">
        {schedules.map((schedule) => (
          <div key={schedule.id} className={`${styles.sitesContainer} w-100 border-bottom`}>
            <span className="fs-6 p-2">
              <span className="text-muted">Visit:</span>
              {' '}{schedule.name}
            </span>
            <div className="d-flex border-top">
              <div className="d-flex align-items-center justify-content-center p-1 border-end">
                <span className="text-muted" style={{ transform: 'rotate(-90deg)' }}>{nameForSites}</span>
              </div>
              <div className={`${styles.sitesCheckboxes} d-flex gap-2 flex-wrap p-2`} style={{ rowGap: '4px' }}>
                {sitesToRender(schedule.id).map((site) => (
                  <div key={site.id} className="d-flex align-items-center justify-content-center">
                    <SiteScheduleCheckbox
                      siteSchedule={siteScheduleMap.get(`${site.id}-${schedule.id}`)}
                      onSelect={handleSiteScheduleSelected}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-end mt-4">
        {!isLoading ? (
          <button className="btn btn-success" onClick={submit}>
            Save
            <span className="btn-addon btn-addon-right">
              <Plus size={16} strokeWidth={2} aria-hidden />
            </span>
          </button>
        ) : (
          <button className="btn btn-success" disabled>
            Saving...
            <span className="btn-addon btn-addon-right">
              <LoaderCircle size={16} strokeWidth={2} className="icon-spin" aria-hidden />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
